import React, { useEffect, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import SEO from '../components/SEO';
import StaticMap from '../components/StaticMap';
import { openPackingSlip } from '../components/PackingSlip';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { Icon } from '../utils/icons';
import { trackEvent } from '../utils/analytics';
import { Skeleton } from '../components/primitives';
import './OrderConfirmation.css';

const OrderConfirmation = () => {
  const { orderNumber } = useParams();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const siteSettings = useSiteSettings();
  const { isAuthenticated } = useCustomerAuth();
  const [order, setOrder] = useState(location.state?.order || null);
  const [loading, setLoading] = useState(!location.state?.order);
  const [error, setError] = useState(null);
  const [guestPassword, setGuestPassword] = useState('');
  const [guestSignupDone, setGuestSignupDone] = useState(false);
  const [guestSignupError, setGuestSignupError] = useState('');
  const [shippingSettings, setShippingSettings] = useState({});

  useEffect(() => {
    api.get('/api/settings/shipping')
      .then(res => { if (res.data) setShippingSettings(res.data); })
      .catch(() => {});
  }, []);

  // GA4 purchase event — the most important conversion signal. Fires
  // once per resolved order, using the order number as transaction_id
  // (GA4 dedupes on this, so refreshes don't double-count revenue).
  // Items pulled from the order itself rather than the cart because
  // the cart may already have been cleared by the redirect from
  // Checkout. Tax + shipping reported separately so GA's Revenue
  // reports can isolate the product subtotal.
  useEffect(() => {
    if (!order || !order.orderNumber) return;
    trackEvent('purchase', {
      transaction_id: order.orderNumber,
      currency: order.currency || 'USD',
      value: parseFloat(order.total) || 0,
      tax: parseFloat(order.tax) || 0,
      shipping: parseFloat(order.shippingCost) || 0,
      coupon: order.couponCode || undefined,
      items: (order.items || []).map(it => ({
        item_id: it.productSku || it.sku || String(it.productId || it.id || ''),
        product_id: it.productId || it.id,
        item_name: it.productName || it.name,
        price: parseFloat(it.price) || 0,
        quantity: it.quantity,
      })),
    });

    // Trustpilot review invitation — best-effort, fires once per confirmed order.
    // Wrapped in try/catch so a Trustpilot outage never breaks the confirmation page.
    try {
      if (window.tp && order?.client?.email) {
        window.tp('invitation', {
          recipientEmail: order.client.email,
          recipientName:  order.client.name  || '',
          referenceId:    order.orderNumber,
          locale:         'en-US',
        });
      }
    } catch (_) {
      // Silent — invitation is non-critical
    }
  }, [order?.orderNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist guest contact + address data so the next checkout visit
  // can pre-fill the form without requiring a login.
  useEffect(() => {
    if (!order) return;
    try {
      const saved = {
        name: order.client?.name || '',
        email: order.client?.email || '',
        phone: order.client?.phone || '',
        street: order.deliveryAddress?.street || '',
        city: order.deliveryAddress?.city || '',
        postalCode: order.deliveryAddress?.postalCode || '',
        country: order.deliveryAddress?.country || siteSettings.storeDefaultCountry || 'United States',
      };
      localStorage.setItem('guest_checkout_data', JSON.stringify(saved));
    } catch (_) {
      // localStorage may be unavailable in private browsing — not critical
    }
  }, [order, siteSettings.storeDefaultCountry]);

  useEffect(() => {
    if (location.state?.order) return;
    if (!orderNumber) {
      setError(t('orderConfirmation.orderNotFoundDetail'));
      setLoading(false);
      return;
    }

    const fetchOrder = async () => {
      try {
        const { data } = await api.get(`/api/orders/lookup/${orderNumber}`);
        setOrder(data);
      } catch {
        setError(t('orderConfirmation.orderNotFoundDetail'));
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderNumber, location.state, t]);

  const statusBadgeClass = (status) => {
    const map = {
      pending: 'status-pending',
      processing: 'status-processing',
      shipped: 'status-shipped',
      delivered: 'status-delivered',
      cancelled: 'status-cancelled',
    };
    return map[status] || 'status-pending';
  };

  const iutePending = order?.paymentMethod === 'iutepay' && order?.paymentStatus !== 'paid';

  if (loading) {
    return (
      <div className="order-confirmation-page container" aria-busy="true">
        <Skeleton.Block height="2rem" width="60%" rounded="md" />
        <Skeleton.Line width="40%" style={{ marginTop: 'var(--space-3)' }} />
        <Skeleton.Block height="200px" rounded="md" style={{ marginTop: 'var(--space-4)' }} />
        <Skeleton.Block height="200px" rounded="md" style={{ marginTop: 'var(--space-4)' }} />
        <p className="sr-only">{t('orderConfirmation.loadingOrder')}</p>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="order-confirmation-page container">
        <SEO title={t('orderConfirmation.orderNotFound')} />
        <div className="error-message-box">
          <span className="error-icon"></span>
          <h2>{t('orderConfirmation.orderNotFound')}</h2>
          <p>{error || t('orderConfirmation.orderNotFoundMessage')}</p>
          <Link to="/" className="btn btn-primary">
            {t('orderConfirmation.goHome')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="order-confirmation-page container">
      <SEO title={`${t('orderConfirmation.orderNumber')} ${order.orderNumber}`} />

      <div className="success-header">
        <div className="success-icon" aria-hidden="true">
          <Icon.Success size={32} strokeWidth={1.75} />
        </div>
        {/* IutePay: the application is in, the loan isn't approved yet. */}
        {iutePending ? (
          <>
            <h1>{t('orderConfirmation.iutepayTitle', 'Application received')}</h1>
            <p className="success-message">
              {t('orderConfirmation.iutepayMessage', 'Your order is waiting for IuteCredit to approve the instalment plan. We will email you as soon as it is approved and then prepare your order.')}
            </p>
          </>
        ) : (
          <>
            <h1>{t('orderConfirmation.successTitle')}</h1>
            <p className="success-message">
              {t('orderConfirmation.successMessage')}
            </p>
          </>
        )}
      </div>

      <div className="order-details-card">
        <div className="order-header">
          <div className="order-number-section">
            <h2>{t('orderConfirmation.orderNumber')} #{order.orderNumber}</h2>
            <span className={`order-status-badge ${statusBadgeClass(order.status)}`}>
              {t(`orderConfirmation.statuses.${order.status}`)}
            </span>
          </div>
          <div className="order-date">
            <span>{new Date(order.createdAt).toLocaleDateString(i18n.language === 'sq' ? 'sq-AL' : 'en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}</span>
          </div>
        </div>

        <div className="order-content">
          {/* Customer & Delivery Info */}
          <div className="info-section">
            <div className="info-card">
              <h3>{t('orderConfirmation.customerInfo')}</h3>
              <div className="info-content">
                <p><strong>{t('orderConfirmation.name')}:</strong> {order.client.name}</p>
                {order.client.email && (
                  <p><strong>{t('orderConfirmation.email')}:</strong> {order.client.email}</p>
                )}
                <p><strong>{t('orderConfirmation.phone')}:</strong> {order.client.phone}</p>
                {order.notificationChannel === 'whatsapp' && (
                  <p className="order-whatsapp-note">
                    {t('orderConfirmation.whatsappUpdates', 'Order updates will arrive on WhatsApp at this phone number.')}
                  </p>
                )}
              </div>
            </div>

            <div className="info-card">
              <h3>{t('orderConfirmation.deliveryAddress')}</h3>
              <div className="info-content">
                <p>{order.deliveryAddress.street}</p>
                <p>
                  {order.deliveryAddress.city}
                  {order.deliveryAddress.state && `, ${order.deliveryAddress.state}`}
                  {' '}{order.deliveryAddress.postalCode}
                </p>
                <p>{order.deliveryAddress.country}</p>
              </div>
              {order.deliveryAddress.latitude && order.deliveryAddress.longitude && (
                <div style={{ marginTop: '0.75rem' }}>
                  <StaticMap
                    latitude={order.deliveryAddress.latitude}
                    longitude={order.deliveryAddress.longitude}
                    width="100%"
                    height={160}
                  />
                </div>
              )}
            </div>
          </div>

          {order.paymentMethod && (
            <div className="info-card" style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', color: '#475569' }}>{t('payment.title', 'Payment Method')}</h3>
              <p style={{ margin: 0, fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}>
                {order.paymentMethod === 'cod' ? t('payment.cod', 'Cash on Delivery') : 
                 order.paymentMethod === 'stripe' ? t('payment.creditCard', 'Credit / Debit Card') :
                 order.paymentMethod === 'iutepay' ? t('checkout.payment.iutepay', 'Pay in instalments with IutePay') :
                 order.paymentMethod === 'bank_transfer' ? t('payment.bankTransfer', 'Bank Transfer') :
                 order.paymentMethod === 'paypal' ? 'PayPal' : order.paymentMethod}
              </p>
              {order.paymentMethod === 'cod' && (
                <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem', color: '#64748b' }}>
                  {t('payment.codConfirmationNote', 'Please have the exact amount ready when your order arrives.')}
                </p>
              )}
            </div>
          )}

          {/* Order Items */}
          <div className="order-items-section">
            <h3>{t('orderConfirmation.orderItems')}</h3>
            <div className="order-items-table">
              {order.items.map((item) => (
                <div key={item.id} className="order-item-row">
                  <div className="item-info">
                    <span className="item-name">{item.productName}</span>
                    {item.productSku && (
                      <span className="item-sku">{t('orderConfirmation.sku')}: {item.productSku}</span>
                    )}
                  </div>
                  <div className="item-quantity">
                    {item.quantity} × {item.price} {siteSettings.currencyLabel || order.currency || t('common.currency')}
                  </div>
                  <div className="item-subtotal">
                    {item.subtotal} {siteSettings.currencyLabel || order.currency || t('common.currency')}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Order Notes */}
          {order.notes && (
            <div className="order-notes">
              <h3>{t('orderConfirmation.orderNotes')}</h3>
              <p>{order.notes}</p>
            </div>
          )}

          {/* Order Totals */}
          {(() => {
            const cur = order.currency || siteSettings.currency || t('common.currency');
            return (
              <div className="order-totals">
                <div className="total-row">
                  <span>{t('common.subtotal')}:</span>
                  <span>{order.subtotal} {cur}</span>
                </div>
                {order.discount && parseFloat(order.discount) > 0 && (
                  <div className="total-row" style={{ color: '#059669' }}>
                    <span>{t('checkout.discount', 'Discount')}{order.couponCode ? ` (${order.couponCode})` : ''}:</span>
                    <span>-{order.discount} {cur}</span>
                  </div>
                )}
                <div className="total-row">
                  <span>{t('common.shipping')}:</span>
                  <span>{parseFloat(order.shippingCost) === 0 ? t('common.freeShipping', 'Free') : `${order.shippingCost} ${cur}`}</span>
                </div>
                <div className="total-row" style={siteSettings.taxIncludedInPrice ? { color: '#64748b', fontSize: '0.875rem' } : undefined}>
                  <span>
                    {siteSettings.taxIncludedInPrice
                      ? t('common.taxIncluded', 'Tax (included)')
                      : t('common.tax', 'Tax')}:
                  </span>
                  <span>{order.tax} {cur}</span>
                </div>
                <div className="total-row total-final">
                  <span>{t('common.total')}:</span>
                  <span>{order.total} {cur}</span>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Next Steps */}
      <div className="next-steps-card">
        <h2>{t('orderConfirmation.whatsNext')}</h2>

        {shippingSettings.estimatedDeliveryDaysMin && shippingSettings.estimatedDeliveryDaysMax && (
          <div style={{ padding: '0.75rem 1rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', marginBottom: '1.25rem', fontSize: '0.938rem', color: '#166534', fontWeight: 600, textAlign: 'center' }}>
            {(() => {
              const min = parseInt(shippingSettings.estimatedDeliveryDaysMin, 10) || 3;
              const max = parseInt(shippingSettings.estimatedDeliveryDaysMax, 10) || 5;
              const fmt = { month: 'short', day: 'numeric' };
              const minDate = new Date(order.createdAt);
              minDate.setDate(minDate.getDate() + min);
              const maxDate = new Date(order.createdAt);
              maxDate.setDate(maxDate.getDate() + max);
              return `Estimated delivery: ${minDate.toLocaleDateString('en-US', fmt)} - ${maxDate.toLocaleDateString('en-US', fmt)}`;
            })()}
          </div>
        )}

        <div className="steps-grid">
          <div className="step-item">
            <span className="step-icon"></span>
            {order.notificationChannel === 'whatsapp' ? (
              <>
                <h3>{t('orderConfirmation.checkWhatsApp.title', 'Check WhatsApp')}</h3>
                <p>{t('orderConfirmation.checkWhatsApp.description', { phone: order.client.phone, defaultValue: 'We will send your order confirmation on WhatsApp to {{phone}}.' })}</p>
              </>
            ) : (
              <>
                <h3>{t('orderConfirmation.checkEmail.title')}</h3>
                <p>{t('orderConfirmation.checkEmail.description', { email: order.client.email })}</p>
              </>
            )}
          </div>
          <div className="step-item">
            <span className="step-icon"></span>
            <h3>{t('orderConfirmation.prepareOrder.title')}</h3>
            <p>{t('orderConfirmation.prepareOrder.description')}</p>
          </div>
          <div className="step-item">
            <span className="step-icon"></span>
            <h3>{t('orderConfirmation.shippingUpdates.title')}</h3>
            <p>{t('orderConfirmation.shippingUpdates.description')}</p>
          </div>
        </div>
      </div>

      {/* Track Order Link */}
      {order.trackingToken && (
        <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '12px', padding: '1.25rem', marginBottom: '1.5rem', textAlign: 'center' }}>
          <p style={{ margin: '0 0 0.75rem', fontWeight: 600, color: '#1e293b' }}>
            {t('orderConfirmation.trackYourOrder', 'Track your order anytime:')}
          </p>
          <Link
            to={`/track/${order.trackingToken}`}
            style={{ display: 'inline-block', padding: '0.75rem 1.5rem', background: '#7c3aed', color: 'white', borderRadius: '8px', fontWeight: 600, textDecoration: 'none', fontSize: '0.938rem' }}
          >
            {t('orderConfirmation.trackOrder', 'Track Order')}
          </Link>
        </div>
      )}

      {/* Guest Account Creation */}
      {!isAuthenticated && order.client?.email && !guestSignupDone && (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '1.25rem', marginBottom: '1.5rem' }}>
          <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.125rem', fontWeight: 700, color: '#166534' }}>
            {t('orderConfirmation.createAccount', 'Create an account')}
          </h3>
          <p style={{ margin: '0 0 1rem', fontSize: '0.875rem', color: '#334155' }}>
            {t('orderConfirmation.createAccountDesc', 'Set a password to track your orders and speed up future checkouts.')}
          </p>
          <form onSubmit={async (e) => {
            e.preventDefault();
            if (guestPassword.length < 6) {
              setGuestSignupError(t('orderConfirmation.passwordTooShort', 'Password must be at least 6 characters'));
              return;
            }
            try {
              await api.post('/api/customer/set-password', { email: order.client.email, orderNumber: order.orderNumber, password: guestPassword });
              setGuestSignupDone(true);
              setGuestSignupError('');
            } catch (err) {
              setGuestSignupError(err.response?.data?.error || 'Failed to create account');
            }
          }} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <input
              type="password"
              value={guestPassword}
              onChange={e => setGuestPassword(e.target.value)}
              placeholder={t('orderConfirmation.choosePassword', 'Choose a password')}
              style={{ flex: '1 1 200px', padding: '0.625rem 0.875rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem' }}
              minLength={6}
              required
            />
            <button type="submit" style={{ padding: '0.625rem 1.25rem', background: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {t('orderConfirmation.setPassword', 'Set Password')}
            </button>
          </form>
          {guestSignupError && <p style={{ color: '#dc2626', fontSize: '0.813rem', marginTop: '0.5rem' }}>{guestSignupError}</p>}
        </div>
      )}
      {guestSignupDone && (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '1.25rem', marginBottom: '1.5rem', textAlign: 'center' }}>
          <p style={{ margin: 0, fontWeight: 600, color: '#166534' }}>
            {t('orderConfirmation.accountCreated', 'Account created! You can now log in to track your orders.')}
          </p>
          <Link to="/login" style={{ display: 'inline-block', marginTop: '0.75rem', padding: '0.5rem 1.25rem', background: '#16a34a', color: 'white', borderRadius: '8px', fontWeight: 600, textDecoration: 'none', fontSize: '0.875rem' }}>
            {t('orderConfirmation.logIn', 'Log In')}
          </Link>
        </div>
      )}

      {/* Action Buttons */}
      <div className="action-buttons">
        <Link to="/products" className="btn btn-primary btn-lg">
          {t('orderConfirmation.continueShopping')}
        </Link>
        <button 
          onClick={() => openPackingSlip(order, siteSettings)} 
          className="btn btn-secondary btn-lg"
        >
          {t('orderConfirmation.printOrder')}
        </button>
        <button 
          onClick={async () => {
            try {
              const res = await api.get(`/api/orders/${order.id}/invoice`, { responseType: 'blob' });
              const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', `invoice-${order.orderNumber}.pdf`);
              document.body.appendChild(link);
              link.click();
              link.remove();
              window.URL.revokeObjectURL(url);
            } catch {
              // Invoice may not be available yet
            }
          }}
          className="btn btn-secondary btn-lg"
        >
          {t('account.downloadInvoice', 'Download Invoice')}
        </button>
      </div>
    </div>
  );
};

export default OrderConfirmation;

